import * as React from 'react';

import { Text } from '@/components/ui';

// DataGridFooter — status caption immediately under the table. Builds
// up from three signals so the user can read the grid's state at a
// glance:
//
//   1. Visible chunk (always present)
//        - Paginated      → "Showing 1–50 of 200"
//        - Filtered only  → "200 of 5000 rows"
//        - Plain          → "5000 rows"
//
//   2. Filter context (only when paginated AND a filter is narrowing
//      the dataset). Appended to the visible chunk as a parenthetical:
//        "Showing 1–50 of 200 (filtered from 5000)"
//      In the non-paginated case the "of total" is already inline in
//      the visible chunk itself, so the parenthetical isn't needed.
//
//   3. Selection chunk (only when at least one row is checked):
//        "· 3 selected"
//
// Returns null when the dataset is empty so the empty-state message
// owns the body without a stray caption underneath.

const DataGridFooter = ({
    filteredCount,
    paginated,
    rangeEnd,
    rangeStart,
    selectedCount,
    totalCount
}) => {
    if (totalCount === 0) {
        return null;
    }

    const isFiltered = filteredCount < totalCount;

    // Build the visible chunk as a single string so the optional
    // parenthetical reads as part of the same phrase (no separator
    // bullet between them).
    let visibleChunk;
    if (paginated) {
        visibleChunk = i18next.t('datagrid.showing_range_of_total', {
            defaultValue: 'Showing {{from}}–{{to}} of {{total}}',
            from: rangeStart,
            to: rangeEnd,
            total: filteredCount
        });
        if (isFiltered) {
            const parenthetical = i18next.t('datagrid.filter_context', {
                defaultValue: '(filtered from {{total}})',
                total: totalCount
            });
            visibleChunk = `${visibleChunk} ${parenthetical}`;
        }
    } else if (isFiltered) {
        visibleChunk = i18next.t('datagrid.showing_of_total', {
            defaultValue: '{{shown}} of {{total}} rows',
            shown: filteredCount,
            total: totalCount
        });
    } else {
        visibleChunk = i18next.t('datagrid.total_count', {
            count: totalCount,
            defaultValue: '{{count}} rows'
        });
    }

    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <Text tone="muted" variant="caption">
                {visibleChunk}
            </Text>
            {selectedCount > 0 ? (
                <Text tone="muted" variant="caption">
                    ·{' '}
                    {i18next.t('datagrid.selected_count', {
                        count: selectedCount,
                        defaultValue: '{{count}} selected'
                    })}
                </Text>
            ) : null}
        </div>
    );
};

export default DataGridFooter;
